import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { Download, Filter } from "lucide-react";
import { addDays, format } from "date-fns";
import { DateRange } from "react-day-picker";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AlertsFiltersProps {
  onFilterChange: (filters: {
    severity?: string;
    category?: string;
    dateRange?: DateRange;
  }) => void;
}

export const AlertsFilters = ({ onFilterChange }: AlertsFiltersProps) => {
  const { toast } = useToast();
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>({
    from: addDays(new Date(), -7),
    to: new Date(),
  });

  const handleExport = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Erro",
          description: "Você precisa estar logado para exportar relatórios",
          variant: "destructive",
        });
        return;
      }

      await supabase.from('report_exports').insert({
        report_type: 'alerts',
        filters: {
          dateRange: dateRange ? {
            from: dateRange.from?.toISOString(),
            to: dateRange.to?.toISOString(),
          } : null,
        },
      });

      // TODO: Implement actual export functionality
      toast({
        title: "Exportação iniciada",
        description: "Seu relatório está sendo gerado e será enviado em breve.",
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      toast({
        title: "Erro na exportação",
        description: "Não foi possível gerar o relatório",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-wrap gap-4 items-center mb-4">
      <Select
        onValueChange={(value) =>
          onFilterChange({ severity: value !== "all" ? value : undefined })
        }
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Filtrar por severidade" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas severidades</SelectItem>
          <SelectItem value="critical">Crítico</SelectItem>
          <SelectItem value="warning">Alerta</SelectItem>
          <SelectItem value="info">Informação</SelectItem>
        </SelectContent>
      </Select>

      <Select
        onValueChange={(value) =>
          onFilterChange({ category: value !== "all" ? value : undefined })
        }
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Filtrar por categoria" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas categorias</SelectItem>
          <SelectItem value="performance">Performance</SelectItem>
          <SelectItem value="security">Segurança</SelectItem>
          <SelectItem value="error">Erro</SelectItem>
          <SelectItem value="system">Sistema</SelectItem>
        </SelectContent>
      </Select>

      <DatePickerWithRange
        date={dateRange}
        onDateChange={(range) => {
          setDateRange(range);
          onFilterChange({ dateRange: range });
        }}
      />

      <Button variant="outline" onClick={handleExport}>
        <Download className="mr-2 h-4 w-4" />
        Exportar
      </Button>
    </div>
  );
};